import { useState } from "react";
import { Bot, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// Manual | Autopilot segmented control. Disabled (with a tooltip) when the
// inbox has no autopilot; switching ON asks for confirmation and lists the rules.
export function AutopilotToggle({ settings, api, onChange }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const available = !!settings?.autopilot_available;
  const on = settings?.mode === "autopilot";

  const setMode = async (mode) => {
    setBusy(true);
    try {
      const s = await api.setSettings({ mode });
      onChange(s);
      toast.success(mode === "autopilot"
        ? "Autopilot on — it handles mail that arrives from now on (checked every 2 min)"
        : "Autopilot off — back to manual handling");
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); }
    finally { setBusy(false); setConfirm(false); }
  };

  const control = (
    <div className={cn("flex items-center gap-0.5 border rounded-md p-0.5",
      available ? (on ? "bg-emerald-50 border-emerald-300" : "bg-slate-50") : "bg-slate-50 opacity-70 cursor-not-allowed")}
      data-testid="autopilot-toggle">
      <button type="button" disabled={!available || busy || !on} onClick={() => setMode("manual")}
        className={cn("px-3 py-1 text-[11px] rounded font-semibold transition-colors",
          !on ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800")}
        data-testid="autopilot-mode-manual">Manual</button>
      <button type="button" disabled={!available || busy || on} onClick={() => setConfirm(true)}
        className={cn("px-3 py-1 text-[11px] rounded font-semibold transition-colors flex items-center gap-1",
          on ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-slate-800")}
        data-testid="autopilot-mode-auto">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}Autopilot
      </button>
    </div>
  );

  return (
    <>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>{control}</TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs max-w-xs">
            {settings?.autopilot_note || "Autopilot for this inbox is configured in Phase 2"}
            {on && settings?.autopilot_since && (
              <div className="mt-1 text-[10px] opacity-80">handling mail received since {new Date(settings.autopilot_since).toLocaleString("en-GB")}</div>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="autopilot-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-emerald-600" />Switch on Autopilot?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm space-y-2">
                <p>From now on, every new email in this inbox is triaged and handled automatically within ~2 minutes:</p>
                <ul className="list-disc pl-5 space-y-1 text-slate-700">
                  {(settings?.autopilot_rules || []).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Finance address: <b>{settings?.finance_forward_to || "not set"}</b> · invoice confidence ≥ {settings?.invoice_min_confidence ?? 90}%.
                  Emails already in the inbox are left for you. Everything it does is listed under the <b>Autopilot log</b> tab.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="autopilot-confirm-cancel">Not now</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); setMode("autopilot"); }} disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="autopilot-confirm-ok">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bot className="w-4 h-4 mr-1" />}Switch on
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
